// VsRingCXPage.js — SEO landing page targeting "RingCX alternative Australia"
// and "RingCentral Contact Centre AU vs" queries. Public, no auth.
// Positions VoiceFabric as a coaching layer for Yabbit UCaaS teams that
// don't want to move to a full contact-centre platform to get AI coaching.
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, DollarSign, Layers, Zap, ShieldCheck, Phone } from 'lucide-react';

const COMPARISON_ROWS = [
  { label: 'Per-user cost (AUD)', ringcx: 'A$115 – A$220 / user / mo (contact-centre licence)', vf: 'A$49 – A$149 / user / mo', highlight: true },
  { label: 'Product category', ringcx: 'Full contact-centre platform (CCaaS)', vf: 'Coaching + intelligence layer on your UCaaS', highlight: true },
  { label: 'Does it replace your phone system?', ringcx: 'Yes — replaces UCaaS AND contact centre', vf: 'No — VoiceFabric sits on Yabbit UCaaS' },
  { label: 'Deployment cycle', ringcx: '3 – 9 months (contact-centre migration)', vf: '4 weeks · no phone-system change' },
  { label: 'Minimum seats', ringcx: 'Typically 25+ · annual contract', vf: '3 seats · A$299/mo min · month-to-month' },
  { label: 'Roles graded', ringcx: 'Contact-centre agents only', vf: 'Sales · Support · Admin · Accounts · Management' },
  { label: 'Includes IVR / queues / workforce mgmt', ringcx: 'Yes — full CCaaS stack', vf: 'No — you keep the queue routing you have today' },
  { label: 'Setup fee', ringcx: 'A$15K – A$60K project quote', vf: 'A$3,500 – A$11,900 fixed' },
  { label: 'Included coaching minutes', ringcx: 'Add-on quoted separately', vf: '60 – 300 mins / user (grading + sentiment)' },
  { label: 'Overage pricing published', ringcx: 'Sales quote', vf: '$0.07/min transcription · $0.09/min sentiment' },
  { label: 'Australian data residency', ringcx: 'Region option · US-first', vf: 'AU-hosted · tenant-isolated' },
  { label: 'White-label / reseller', ringcx: 'Partner tier only', vf: '50% reseller margin available' },
];

const Chip = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ok ? 'bg-teal-500/15 text-teal-300' : 'bg-red-500/15 text-red-300'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const VsRingCXPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="vs-ringcx-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/why-voicefabric" className="text-white/60 hover:text-white">Why VoiceFabric</Link>
          <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 font-semibold" data-testid="vs-ringcx-signin">Sign in →</Link>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
      <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">RingCX · Alternative for Australian teams</p>
      <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
        You don&apos;t need a full contact centre<br /><span className="text-teal-400">to get world-class call coaching.</span>
      </h1>
      <p className="text-white/70 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
        RingCX is a proper contact-centre platform — IVR, WFM, omnichannel, the works. It&apos;s a fantastic product if you actually run a 100-seat contact centre.
        Most Australian SMB teams don&apos;t. They run 10-40 phone-heavy people and just need <strong className="text-white">the coaching, grading and governance layer</strong> —
        without the six-figure migration.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-ringcx-pricing-cta"
        >
          See our pricing
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/free-audit"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-ringcx-audit-cta"
        >
          Free 3-call audit
        </Link>
      </div>
    </section>

    {/* At-a-glance callouts */}
    <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-4">
      {[
        { icon: Phone, title: 'Keep your Yabbit UCaaS', body: 'No number porting, no queue rebuild, no WFM project. VoiceFabric extends the phone system you already run.' },
        { icon: DollarSign, title: 'Half the per-user cost — no CCaaS licence', body: 'A$49-149/user/mo, no contact-centre platform fee, no 25-seat floor, no A$60K setup.' },
        { icon: Layers, title: 'Coaching without the platform', body: 'You get the coaching + governance layer RingCX bundles at the top — without buying the whole stack underneath it.' },
      ].map((c) => (
        <div key={c.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <c.icon className="w-6 h-6 text-teal-400 mb-3" />
          <h3 className="text-white font-bold text-lg">{c.title}</h3>
          <p className="text-white/60 text-sm mt-1">{c.body}</p>
        </div>
      ))}
    </section>

    {/* Comparison table */}
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="vs-ringcx-comparison">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Side-by-side · RingCX vs VoiceFabric</h2>
        <p className="text-white/50 text-sm mt-3">Pricing as of Feb 2026, sourced from RingCentral&apos;s public pricing page + AU reseller quotes.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/60 text-xs uppercase tracking-wider bg-white/5">
              <th className="px-4 py-3 text-left">Feature</th>
              <th className="px-4 py-3 text-left">RingCX (contact-centre)</th>
              <th className="px-4 py-3 text-left text-teal-300">Yabbit VoiceFabric</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {COMPARISON_ROWS.map((r) => (
              <tr key={r.label} className={r.highlight ? 'bg-teal-500/5' : ''}>
                <td className="px-4 py-3 text-white/80 font-medium">{r.label}</td>
                <td className="px-4 py-3 text-white/50"><Chip ok={false}>{r.ringcx}</Chip></td>
                <td className="px-4 py-3"><Chip ok>{r.vf}</Chip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-white/30 text-[11px] mt-4 italic text-center">
        RingCX AU pricing sourced from ringcentral.com/au public pricing + partner quotes for &lt;50-seat deployments.
        Feb 2026 snapshot — check ringcentral.com/au for latest.
      </p>
    </section>

    {/* Honest — when RingCX is the right pick */}
    <section className="max-w-3xl mx-auto px-6 py-12">
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
        <h3 className="text-amber-300 font-bold text-lg mb-3">Honest answer: when RingCX is the right choice</h3>
        <p className="text-white/70 leading-relaxed">
          If you actually run a <strong className="text-white">50+ seat contact centre</strong> with omnichannel volume, formal WFM,
          skill-based routing across dozens of queues, and CX reporting SLAs — RingCX is a real, mature platform and worth the investment.
        </p>
        <p className="text-white/70 leading-relaxed mt-3">
          If your business is <strong className="text-white">phone-heavy but not a contact centre</strong> — 10-40 people spread across
          sales, support, admin and accounts, all just using Yabbit day-to-day — you&apos;re paying for infrastructure you don&apos;t need.
          VoiceFabric gives you the coaching + governance without the platform layer.
        </p>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="max-w-4xl mx-auto px-6 py-16 text-center">
      <div className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-3xl p-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
          Keep the phone system that works.<br />Skip the contact-centre migration.
        </h2>
        <p className="text-white/70 text-lg mt-4 max-w-xl mx-auto">
          Upload three of your recordings. We&apos;ll grade them against a role-specific knowledge base and show you the coaching output — no CCaaS licence required.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/free-audit"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors"
            data-testid="vs-ringcx-final-cta"
          >
            Get my free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-4"
          >
            See pricing →
          </Link>
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-8 text-center">
        <div>
          <ShieldCheck className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Australian data residency</h3>
          <p className="text-white/50 text-sm mt-1">AU-hosted · tenant-isolated · Privacy Act 2026 ready</p>
        </div>
        <div>
          <Zap className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Live in 4 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Onboarding call · KB workshop · baseline scoring by week 3</p>
        </div>
        <div>
          <Layers className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">5-role grading</h3>
          <p className="text-white/50 text-sm mt-1">Sales, support, admin, accounts, management — one platform</p>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/why-voicefabric" className="hover:text-white/50">Why VoiceFabric</Link> · <Link to="/vs-dialpad" className="hover:text-white/50">vs Dialpad</Link> · <Link to="/vs-aircall" className="hover:text-white/50">vs Aircall</Link>
    </footer>
  </div>
);

export default VsRingCXPage;
